import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { NativeSelectOption } from '@shared/ui/fields'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { LessonTutorAvailabilityItem, LessonTutorFilters } from './interfaces'

import { Type as BookingType } from '@brighterly/lib-core-types/booking/enums'
import { PaidLabel, TutorCategoryType } from '@brighterly/lib-core-types/tutor/enums'
import { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { LessonTutorAvailability, LessonTutorSpecialFeature } from './enums'

interface LessonTypePrivileges {
  canManageBookings: boolean
  canManageDemoBookings: boolean
}

const dayjs = useDayjs()

const PAID_CATEGORIES: TutorCategoryType[] = [TutorCategoryType.PaidMath, TutorCategoryType.PaidEla]
const DEMO_CATEGORIES: TutorCategoryType[] = [TutorCategoryType.DemoMath, TutorCategoryType.DemoEla]

/** The API refuses a type the admin cannot manage (403), so it is not offered: paid lessons need manage-bookings,
 *  demo and paid demo lessons manage-demo-bookings. EVALUATION is never created here. */
export const getAllowedLessonTypes = ({ canManageBookings, canManageDemoBookings }: LessonTypePrivileges): BookingType[] => [
  ...(canManageBookings ? [BookingType.Paid] : []),
  ...(canManageDemoBookings ? [BookingType.Demo, BookingType.PaidDemo] : []),
]

export const getDefaultLessonType = (allowed: BookingType[]): BookingType | null =>
  allowed.includes(BookingType.Paid) ? BookingType.Paid : allowed[0] ?? null

/** In the order of the configured list, like the legacy select. */
export const buildLessonTypeOptions = (
  labels: Record<string, string>,
  allowed: BookingType[],
  selected: BookingType | null,
): NativeSelectOption[] =>
  Object.entries(labels)
    .filter(([value]) => allowed.includes(value as BookingType))
    .map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === selected,
    }))

/** Without applied category filters the tutors are looked up among the categories of the lesson type. */
export const getLessonTypeCategories = (type: BookingType | null): TutorCategoryType[] =>
  type === BookingType.Paid ? PAID_CATEGORIES : DEMO_CATEGORIES

/** The tutor filters start on the discipline's paid category with the grade feature ticked, as the legacy panel did. */
/** A tutor filter's options from its labels list: value is the key, text the label. */
export const buildTutorFilterOptions = (labels: Record<string, string> | undefined): AppSelectOption<string>[] =>
  Object.entries(labels ?? {}).map(([value, text]) => ({ value, text }))

export const getDefaultTutorFilters = (code: string | null | undefined): LessonTutorFilters => ({
  gender: [],
  special_features: [LessonTutorSpecialFeature.Grade],
  accent: [],
  categories: [code === Discipline.ELA ? TutorCategoryType.PaidEla : TutorCategoryType.PaidMath],
})

/** The count next to "Tutor filters" — categories are not counted, as in the legacy panel. */
export const countTutorFilters = (filters: LessonTutorFilters): number =>
  filters.gender.length + filters.special_features.length + filters.accent.length

/** The legacy "-- Not selected --" row of the tutor select: the select cannot hold null, so it carries this id. */
export const LESSON_NO_TUTOR_ID = 0

export const LESSON_NO_TUTOR_OPTION: AppSelectOption<number> = { value: LESSON_NO_TUTOR_ID, text: '-- Not selected --' }

/** Busy tutors stay in the list disabled; a free tutor outside their working hours can still be chosen. */
export const buildLessonTutorOptions = (
  tutors: LessonTutorAvailabilityItem[],
  paidLabels: Record<string, string>,
): AppSelectOption<number>[] =>
  tutors.map((tutor) => {
    const topLabel = tutor.paid_label === PaidLabel.Top ? ` (${paidLabels[PaidLabel.Top] ?? PaidLabel.Top})` : ''
    const offSchedule = tutor.availability === LessonTutorAvailability.OutOfSchedule ? ' (off schedule)' : ''

    return {
      value: tutor.id,
      text: `${tutor.full_name}${topLabel}${offSchedule}`,
      disabled: tutor.availability === LessonTutorAvailability.Unavailable,
    }
  })

export const getTutorAvailability = (tutors: LessonTutorAvailabilityItem[], tutorId: number | null): LessonTutorAvailability | null =>
  tutors.find(tutor => tutor.id === tutorId)?.availability ?? null

export interface LessonTutorAvailabilityLabel {
  text: string
  color: AppBadgeColor
}

/** The badge beside the tutor select of a lesson modal: what the pick means at the chosen time. */
export const TUTOR_AVAILABILITY_LABELS: Record<LessonTutorAvailability, LessonTutorAvailabilityLabel> = {
  [LessonTutorAvailability.Available]: { text: 'Available', color: 'emerald' },
  [LessonTutorAvailability.OutOfSchedule]: { text: 'Off schedule', color: 'amber' },
  [LessonTutorAvailability.Unavailable]: { text: 'Busy at this time', color: 'red' },
}

export const isTutorSelectable = (tutors: LessonTutorAvailabilityItem[], tutorId: number | null): boolean =>
  tutors.some(tutor => tutor.id === tutorId && tutor.availability !== LessonTutorAvailability.Unavailable)

/** The tutor's week calendar opens on the Monday of the lesson's week. */
export const getTutorScheduleWeekFrom = (date: string): string =>
  dayjs(date, DateFormat.DefaultDate).startOf('isoWeek').startOf('day').format(DateFormat.Default)
